/**
 * React-query hooks over the dashboard file endpoints: the project's list of
 * dashboard names, one normalised document, and the save / create / remove /
 * rename / duplicate mutations. Every mutation refreshes the list; save also
 * writes the saved document into the detail cache so the editor does not
 * re-fetch what it just wrote.
 */
import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { AvailableDashboards, getDashboard, saveDashboard, deleteDashboard } from "api/Repos/dashboard"
import { DashboardDocument, createEmptyDocument, normalizeDashboardDocument, NormalizeResult } from "features/Dashboard/types/dashboardDocument"

/** Newline-separated names to a trimmed, de-duplicated, case-insensitively sorted list. */
export const parseDashboardList = (text: unknown): string[] => {
  if (typeof text !== "string") return []
  return Array.from(new Set(text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
}

/**
 * The Conductor answers 200 even when the Repos server refuses a request (for
 * example `{"Err msg":"Error 1019: Unable to save project file"}`), so a
 * resolved request is not proof of success. Throws `what: <message>` when the
 * body carries an `Err msg` string or a non-zero numeric `Err code`. Pure:
 * callers surface the error.
 */
export const assertNoEngineError = (body: unknown, what: string): void => {
  if (typeof body !== "object" || body === null) return
  const rec = body as Record<string, unknown>
  const msg = typeof rec["Err msg"] === "string" ? rec["Err msg"].trim() : null
  const code = typeof rec["Err code"] === "number" ? rec["Err code"] : 0
  if (msg === null && code === 0) return
  throw new Error(`${what}: ${msg || (code !== 0 ? `Error ${code}` : "Unknown error")}`)
}

/** The project's dashboard names; an error body throws rather than reading as an empty list. */
const fetchDashboardNames = async (getAxios: ReturnType<typeof useAuthedAxios>, projectId: string, owner: string): Promise<string[]> => {
  const body: unknown = (await AvailableDashboards(await getAxios(), projectId, owner)).data
  assertNoEngineError(body, "Could not list the dashboards")
  return parseDashboardList(body)
}

export const useDashboardList = (projectId: string, owner: string, enabled = true) => {
  const getAxios = useAuthedAxios()
  return useQuery({
    queryKey: queryKeys.dashboards.list(projectId),
    enabled: enabled && Boolean(projectId && owner),
    queryFn: () => fetchDashboardNames(getAxios, projectId, owner),
    staleTime: 5_000,
  })
}

export const useDashboardDocument = (projectId: string, owner: string, name: string, enabled = true) => {
  const getAxios = useAuthedAxios()
  return useQuery<NormalizeResult>({
    queryKey: queryKeys.dashboards.detail(projectId, name),
    enabled: enabled && Boolean(projectId && owner && name),
    queryFn: async () => {
      const body = (await getDashboard(await getAxios(), projectId, owner, name)).data
      assertNoEngineError(body, `Could not load "${name}"`)
      return normalizeDashboardDocument(body)
    },
    staleTime: Infinity,
    retry: false,
  })
}

export const useDashboardMutations = (projectId: string, owner: string) => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.list(projectId) })

  const saveMutation = useMutation({
    mutationFn: async ({ name, doc }: { name: string; doc: DashboardDocument }) => {
      const stamped = { ...doc, updatedAt: Date.now() }
      const res = await saveDashboard(await getAxios(), projectId, owner, JSON.stringify(stamped), name, true)
      // Throwing here keeps onSuccess from caching a document that was never stored.
      assertNoEngineError(res.data, `Could not save "${name}"`)
      return stamped
    },
    onSuccess: (stamped, { name }) => {
      // Typed const: inline, `ok: true` widens to boolean inside the Updater union and fails to narrow.
      const cached: NormalizeResult = { ok: true, document: stamped }
      queryClient.setQueryData<NormalizeResult>(queryKeys.dashboards.detail(projectId, name), cached)
      void invalidate()
    },
  })

  /**
   * Saves always overwrite, so writing under a new name first checks the
   * current list (fresh from the server) and refuses a case-insensitive match.
   */
  const assertNameFree = useCallback(async (name: string) => {
    const existing = await fetchDashboardNames(getAxios, projectId, owner)
    const target = name.toLowerCase()
    if (existing.some((n) => n.toLowerCase() === target)) {
      throw new Error(`A dashboard called "${name}" already exists.`)
    }
  }, [getAxios, projectId, owner])

  const save = useCallback((name: string, doc: DashboardDocument) => saveMutation.mutateAsync({ name, doc }).then(() => undefined), [saveMutation.mutateAsync])
  const create = useCallback(async (name: string) => {
    await assertNameFree(name)
    await saveMutation.mutateAsync({ name, doc: createEmptyDocument(name) })
  }, [assertNameFree, saveMutation.mutateAsync])
  const remove = useCallback(async (name: string) => {
    const res = await deleteDashboard(await getAxios(), projectId, owner, name)
    assertNoEngineError(res.data, `Could not delete "${name}"`)
    queryClient.removeQueries({ queryKey: queryKeys.dashboards.detail(projectId, name) })
    await invalidate()
  }, [getAxios, projectId, owner, queryClient])
  /**
   * Copies `from` to `to` with a fresh title and creation time. Refuses a
   * `to` that already exists, and old Reporting files.
   */
  const duplicate = useCallback(async (from: string, to: string) => {
    await assertNameFree(to)
    const body = (await getDashboard(await getAxios(), projectId, owner, from)).data
    assertNoEngineError(body, `Could not load "${from}"`)
    const res = normalizeDashboardDocument(body)
    if (!res.ok) throw new Error("This dashboard was saved in an older format and cannot be duplicated.")
    await saveMutation.mutateAsync({ name: to, doc: { ...res.document, title: to, createdAt: Date.now() } })
  }, [assertNameFree, getAxios, projectId, owner, saveMutation.mutateAsync])
  /** There is no rename endpoint: duplicate to the new name, then remove the old file. */
  const rename = useCallback(async (from: string, to: string) => {
    await duplicate(from, to)
    await remove(from)
  }, [duplicate, remove])

  return { save, create, remove, rename, duplicate, isSaving: saveMutation.isPending }
}
